import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  CalendarDays,
  Search,
  Phone,
  Mail,
  Activity,
  CheckCircle2,
  Clock3,
  CircleDashed,
  Eye,
  X,
} from "lucide-react";

const API_BASE = "https://api.elitecarephysiorehab.com/api/appointments";

export default function AdminAppointments() {
  const { authFetch } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({
    totalAppointments: 0,
    todayAppointments: 0,
    pendingAppointments: 0,
    confirmedAppointments: 0,
    completedAppointments: 0,
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const fetchAppointments = useCallback(async () => {
    try {
      const query = new URLSearchParams();
      if (statusFilter !== "All") query.append("status", statusFilter);
      if (search.trim()) query.append("search", search.trim());

      const response = await authFetch(`${API_BASE}?${query.toString()}`);
      const result = await response.json();
      setAppointments(result.data || []);
    } catch (error) {
      console.error(error);
    }
  }, [authFetch, statusFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/dashboard/stats`);
      const result = await response.json();
      setStats(result.data || {});
    } catch (error) {
      console.error(error);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const updateStatus = async (id, status) => {
    try {
      await authFetch(`${API_BASE}/${id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });

      await fetchAppointments();
      await fetchStats();

      if (selectedAppointment?.id === id) {
        setSelectedAppointment((prev) => ({ ...prev, status }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        .admin-page {
          padding: 0;
        }
        .admin-wrapper {
          max-width: 1350px;
          margin: 0 auto;
        }
        .admin-top {
          margin-bottom: 24px;
        }
        .admin-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(34, 197, 94, 0.10);
          color: #15803d;
          border: 1px solid rgba(34, 197, 94, 0.18);
          padding: 8px 16px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          margin-bottom: 14px;
        }
        .admin-top h1 {
          font-size: 32px;
          line-height: 1.2;
          color: #0f172a;
          margin-bottom: 10px;
          font-weight: 800;
        }
        .admin-top h1 span { color: #16a34a; }
        .admin-top p {
          color: #64748b;
          font-size: 15px;
          line-height: 1.7;
          max-width: 100%;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 18px;
          margin-bottom: 28px;
        }
        .stat-card {
          background: #fff;
          border: 1px solid #edf2ef;
          border-radius: 24px;
          padding: 22px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
        }
        .stat-icon {
          width: 46px;
          height: 46px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
          background: rgba(34, 197, 94, 0.10);
          color: #16a34a;
        }
        .stat-title {
          color: #64748b;
          font-size: 14px;
          margin-bottom: 8px;
          font-weight: 600;
        }
        .stat-value {
          font-size: 28px;
          font-weight: 800;
          color: #0f172a;
        }
        .toolbar {
          background: #fff;
          border: 1px solid #edf2ef;
          border-radius: 24px;
          padding: 20px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
          display: grid;
          grid-template-columns: 1fr 220px;
          gap: 16px;
          margin-bottom: 24px;
        }
        .search-box {
          position: relative;
        }
        .search-icon {
          position: absolute;
          top: 50%;
          left: 14px;
          transform: translateY(-50%);
          color: #16a34a;
          width: 18px;
          height: 18px;
        }
        .search-input,
        .filter-select {
          width: 100%;
          border: 1px solid #dbe5dd;
          border-radius: 16px;
          padding: 14px 16px 14px 44px;
          font-size: 15px;
          color: #0f172a;
          background: #f8fbf8;
          outline: none;
        }
        .filter-select {
          padding-left: 16px;
          cursor: pointer;
        }
        .table-card {
          background: #fff;
          border: 1px solid #edf2ef;
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
        }
        .table-head {
          padding: 22px 24px;
          border-bottom: 1px solid #edf2ef;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .table-head h3 {
          margin: 0;
          font-size: 24px;
          font-weight: 800;
          color: #0f172a;
        }
        .table-head p {
          margin: 6px 0 0;
          font-size: 14px;
          color: #64748b;
        }
        .table-scroll {
          width: 100%;
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          min-width: 1100px;
        }
        thead th {
          text-align: left;
          padding: 16px 20px;
          background: #f8fbf8;
          color: #475569;
          font-size: 13px;
          font-weight: 800;
          border-bottom: 1px solid #edf2ef;
        }
        tbody td {
          padding: 18px 20px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          font-size: 14px;
          vertical-align: top;
        }
        .patient-name {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 6px;
        }
        .sub-info {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #64748b;
          font-size: 13px;
          margin-bottom: 5px;
        }
        .service-pill {
          display: inline-block;
          padding: 8px 12px;
          border-radius: 999px;
          background: #eefbf1;
          color: #15803d;
          font-size: 12px;
          font-weight: 700;
          border: 1px solid #d9f6df;
        }
        .status-pill {
          display: inline-block;
          padding: 8px 12px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 800;
          border: 1px solid transparent;
        }
        .status-pending {
          background: #fff7ed;
          color: #c2410c;
          border-color: #fed7aa;
        }
        .status-confirmed {
          background: #ecfdf3;
          color: #15803d;
          border-color: #bbf7d0;
        }
        .status-completed {
          background: #eff6ff;
          color: #1d4ed8;
          border-color: #bfdbfe;
        }
        .actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .action-btn {
          border: none;
          border-radius: 12px;
          padding: 10px 12px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
        }
        .btn-pending { background: #fff7ed; color: #c2410c; }
        .btn-confirmed { background: #ecfdf3; color: #15803d; }
        .btn-completed { background: #eff6ff; color: #1d4ed8; }
        .btn-view { background: #f8fafc; color: #334155; }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          z-index: 9999;
          overflow-y: auto;
        }
        .modal-card {
          width: 100%;
          max-width: 760px;
          max-height: 90vh;
          background: #fff;
          border-radius: 28px;
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          margin: auto;
        }
        .modal-head {
          padding: 18px 20px;
          border-bottom: 1px solid #edf2ef;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: sticky;
          top: 0;
          background: #fff;
          z-index: 1;
        }
        .modal-head h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
        }
        .close-btn {
          border: none;
          background: #f1f5f9;
          border-radius: 12px;
          padding: 10px 14px;
          cursor: pointer;
          flex-shrink: 0;
        }
        .modal-body {
          padding: 20px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
        }
        .detail-card {
          background: #f8fbf8;
          border: 1px solid #e8f3ea;
          border-radius: 18px;
          padding: 16px;
        }
        .detail-card.full {
          grid-column: 1 / -1;
        }
        .detail-label {
          font-size: 12px;
          color: #64748b;
          font-weight: 800;
          margin-bottom: 8px;
          text-transform: uppercase;
        }
        .detail-value {
          font-size: 15px;
          color: #0f172a;
          line-height: 1.7;
          font-weight: 600;
        }

        @media (max-width: 1200px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        @media (max-width: 900px) {
          .toolbar {
            grid-template-columns: 1fr;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .modal-body {
            grid-template-columns: 1fr;
          }
          .table-head {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }
          .table-head h3 {
            font-size: 18px;
          }
        }
        @media (max-width: 600px) {
          .admin-top h1 {
            font-size: 24px;
          }
          .stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          .stat-card {
            padding: 14px;
            border-radius: 16px;
          }
          .stat-value {
            font-size: 22px;
          }
          .toolbar {
            padding: 14px;
            border-radius: 16px;
          }
          .modal-card {
            border-radius: 20px;
          }
          .modal-head h3 {
            font-size: 18px;
          }
          .modal-body {
            padding: 16px;
            gap: 12px;
          }
        }
        @media (max-width: 400px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
          .admin-top h1 {
            font-size: 20px;
          }
        }
      `}</style>

      <section className="admin-page">
        <div className="admin-wrapper">
          <div className="admin-top">
            <div className="admin-badge">
              <CalendarDays size={15} />
              APPOINTMENT ADMIN PANEL
            </div>

            <h1>
              Appointment <span>Dashboard</span>
            </h1>

            <p>
              Manage all patient bookings, review appointment details, and update
              booking status from one clean admin panel.
            </p>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon"><CalendarDays size={22} /></div>
              <div className="stat-title">Total Appointments</div>
              <div className="stat-value">{stats.totalAppointments}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon"><Activity size={22} /></div>
              <div className="stat-title">Today Appointments</div>
              <div className="stat-value">{stats.todayAppointments}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon"><CircleDashed size={22} /></div>
              <div className="stat-title">Pending</div>
              <div className="stat-value">{stats.pendingAppointments}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon"><Clock3 size={22} /></div>
              <div className="stat-title">Confirmed</div>
              <div className="stat-value">{stats.confirmedAppointments}</div>
            </div>

            <div className="stat-card">
              <div className="stat-icon"><CheckCircle2 size={22} /></div>
              <div className="stat-title">Completed</div>
              <div className="stat-value">{stats.completedAppointments}</div>
            </div>
          </div>

          <div className="toolbar">
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search by patient name, phone, or service"
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Completed">Completed</option>
            </select>
          </div>

          <div className="table-card">
            <div className="table-head">
              <div>
                <h3>Appointment List</h3>
                <p>Showing {appointments.length} appointments</p>
              </div>
            </div>

            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Appointment</th>
                    <th>Service</th>
                    <th>Therapist</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {appointments.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="patient-name">{item.fullName}</div>
                        <div className="sub-info">
                          <Phone size={14} />
                          <span>{item.phone}</span>
                        </div>
                        <div className="sub-info">
                          <Mail size={14} />
                          <span>{item.email || "No email"}</span>
                        </div>
                      </td>

                      <td>
                        <div className="sub-info">
                          <CalendarDays size={14} />
                          <span>{item.appointmentDate}</span>
                        </div>
                        <div className="sub-info">
                          <Clock3 size={14} />
                          <span>{item.preferredTime}</span>
                        </div>
                      </td>

                      <td>
                        <span className="service-pill">{item.service}</span>
                      </td>

                      <td>{item.therapist || "Not specified"}</td>

                      <td>
                        <span
                          className={`status-pill ${
                            item.status === "Pending"
                              ? "status-pending"
                              : item.status === "Confirmed"
                              ? "status-confirmed"
                              : "status-completed"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>

                      <td>
                        <div className="actions">
                          <button
                            className="action-btn btn-pending"
                            onClick={() => updateStatus(item.id, "Pending")}
                          >
                            Pending
                          </button>
                          <button
                            className="action-btn btn-confirmed"
                            onClick={() => updateStatus(item.id, "Confirmed")}
                          >
                            Confirm
                          </button>
                          <button
                            className="action-btn btn-completed"
                            onClick={() => updateStatus(item.id, "Completed")}
                          >
                            Complete
                          </button>
                          <button
                            className="action-btn btn-view"
                            onClick={() => setSelectedAppointment(item)}
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {selectedAppointment && (
        <div className="modal-overlay" onClick={() => setSelectedAppointment(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Appointment Details</h3>
              <button
                className="close-btn"
                onClick={() => setSelectedAppointment(null)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-card">
                <div className="detail-label">Patient Name</div>
                <div className="detail-value">{selectedAppointment.fullName}</div>
              </div>

              <div className="detail-card">
                <div className="detail-label">Phone Number</div>
                <div className="detail-value">{selectedAppointment.phone}</div>
              </div>

              <div className="detail-card">
                <div className="detail-label">Email</div>
                <div className="detail-value">
                  {selectedAppointment.email || "No email"}
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-label">Appointment Date</div>
                <div className="detail-value">{selectedAppointment.appointmentDate}</div>
              </div>

              <div className="detail-card">
                <div className="detail-label">Preferred Time</div>
                <div className="detail-value">{selectedAppointment.preferredTime}</div>
              </div>

              <div className="detail-card">
                <div className="detail-label">Service</div>
                <div className="detail-value">{selectedAppointment.service}</div>
              </div>

              <div className="detail-card">
                <div className="detail-label">Therapist</div>
                <div className="detail-value">
                  {selectedAppointment.therapist || "Not specified"}
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-label">Status</div>
                <div className="detail-value">{selectedAppointment.status}</div>
              </div>

              <div className="detail-card full">
                <div className="detail-label">Symptoms / Message</div>
                <div className="detail-value">
                  {selectedAppointment.message || "No message"}
                </div>
              </div>

              <div className="detail-card full">
                <div className="detail-label">Quick Actions</div>
                <div className="actions">
                  <button
                    className="action-btn btn-pending"
                    onClick={() => updateStatus(selectedAppointment.id, "Pending")}
                  >
                    Pending
                  </button>
                  <button
                    className="action-btn btn-confirmed"
                    onClick={() => updateStatus(selectedAppointment.id, "Confirmed")}
                  >
                    Confirm
                  </button>
                  <button
                    className="action-btn btn-completed"
                    onClick={() => updateStatus(selectedAppointment.id, "Completed")}
                  >
                    Complete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}